import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type AtomicModel } from "@/lib/forex-api";

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function ModelDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modelId = searchParams.get("id") || "";
  const [model, setModel] = useState<AtomicModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    forexApi
      .listAtomicModels()
      .then((result) => {
        if (cancelled) return;
        const matched = result.items.find((item) => item.atomic_model_id === modelId) || null;
        setModel(matched);
        if (!matched) setError("未找到原子模型，请从模型配置列表重新进入。");
      })
      .catch(() => {
        if (!cancelled) setError("原子模型接口不可用，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "智能体运营", href: "/forex/agents/model-config" }, { label: "模型详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">模型配置详情</h2>
          <p className="text-xs text-text-muted mt-0.5">{model ? `${model.provider} · ${model.model_id} · ${model.atomic_model_id}` : modelId || "未选择模型"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/forex/agents/model-config")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回列表</button>
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && model && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">状态</p><p className={`text-xs font-semibold mt-1 ${model.status === "enabled" ? "text-emerald-500" : "text-red-500"}`}>{model.status === "enabled" ? "启用" : "禁用"}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">月调用限额</p><p className="text-lg font-semibold font-mono text-text-primary">{model.monthly_limit.toLocaleString()}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">成本/1K tokens</p><p className="text-lg font-semibold font-mono text-text-primary">${model.cost_per_1k_tokens}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">平均延迟</p><p className="text-lg font-semibold font-mono text-text-primary">{model.latency_ms}ms</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">绑定智能体</p><p className="text-lg font-semibold font-mono text-text-primary">{model.bound_agent_count}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">更新时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(model.updated_at)}</p></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-surface-border p-5">
              <h3 className="text-sm font-semibold text-text-primary mb-3">基本信息</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "模型ID", value: model.atomic_model_id, mono: true },
                  { label: "提供方", value: model.provider },
                  { label: "模型名称", value: model.model_id, mono: true },
                  { label: "显示名称", value: model.display_name },
                  { label: "用途", value: model.purpose },
                  { label: "状态", value: model.status },
                ].map((row) => (
                  <div key={row.label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted font-medium uppercase">{row.label}</span>
                    <p className={`text-xs text-text-primary mt-0.5 ${row.mono ? "font-mono" : ""}`}>{row.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-text-muted mt-4">原子模型资源仅供查看；策略、Prompt 和 AI 角色在策略模板中配置。</p>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-5">
              <h3 className="text-sm font-semibold text-text-primary mb-3">下游引用</h3>
              <div className="space-y-2">
                <Link to="/forex/strategy-templates" className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer"><span className="text-xs text-text-secondary">策略角色配置</span><i className="ri-arrow-right-line text-text-muted" /></Link>
                <Link to="/forex/agents/create" className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer"><span className="text-xs text-text-secondary">创建智能体</span><i className="ri-arrow-right-line text-text-muted" /></Link>
                <Link to="/forex/model-usage" className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer"><span className="text-xs text-text-secondary">模型调用大盘</span><i className="ri-arrow-right-line text-text-muted" /></Link>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
